import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Button,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  makeStyles,
} from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import {
  grayColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";

const useStyles = makeStyles(() => ({
  filterButton: {
    minWidth: "auto !important",
    padding: "4px 8px !important",
    fontSize: "0.75rem !important",
    textTransform: "none !important",
    color: `${grayColor[3]} !important`,
    border: `1px solid ${grayColor[8]} !important`,
    borderRadius: "4px !important",
    backgroundColor: `${whiteColor} !important`,
    fontWeight: "500 !important",
    "&:hover": {
      backgroundColor: `${grayColor[12]} !important`,
      borderColor: `${grayColor[6]} !important`,
    },
    "& *": {
      backgroundColor: "transparent !important",
    },
  },
  filterButtonActive: {
    backgroundColor: (props) => `${props.clientColor}15 !important`,
    borderColor: (props) => `${props.clientColor} !important`,
    color: (props) => `${props.clientColor} !important`,
    "&:hover": {
      backgroundColor: (props) => `${props.clientColor}25 !important`,
    },
  },
  filterIcon: {
    fontSize: "18px !important",
    marginRight: "0 !important",
    backgroundColor: "transparent !important",
  },
  filterCount: {
    marginLeft: "2px !important",
    fontSize: "0.65rem !important",
    backgroundColor: (props) => `${props.clientColor} !important`,
    color: `${whiteColor} !important`,
    borderRadius: "50% !important",
    minWidth: "14px !important",
    height: "14px !important",
    padding: "0 2px !important",
    display: "inline-flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",
  },
  menu: {
    "& .MuiPaper-root": {
      marginTop: 4,
      minWidth: 150,
      maxHeight: 300,
    },
  },
  menuItem: {
    fontSize: "0.8rem",
    padding: "4px 8px",
    "&:hover": {
      backgroundColor: grayColor[12],
    },
  },
  checkbox: {
    padding: 4,
    marginRight: 4,
    "& .MuiSvgIcon-root": {
      fontSize: "1.1rem",
    },
  },
  clearItem: {
    borderTop: `1px solid ${grayColor[6]}`,
    marginTop: "4px",
    paddingTop: "8px",
    color: `${grayColor[2]} !important`,
    fontWeight: "500 !important",
    justifyContent: "center",
    fontSize: "0.8rem",
    "&:hover": {
      backgroundColor: `${grayColor[11]} !important`,
    },
  },
}));

const VariantTagFilter = ({
  availableTags,
  selectedTags,
  onTagsChange,
  clientColor,
}) => {
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleTag = (tag) => {
    const currentTags = selectedTags || [];
    if (currentTags.includes(tag)) {
      onTagsChange(currentTags.filter((t) => t !== tag));
    } else {
      onTagsChange([...currentTags, tag]);
    }
  };

  const handleClearAll = () => {
    onTagsChange([]);
    handleClose();
  };

  const isActive = selectedTags && selectedTags.length > 0;

  if (availableTags.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        className={`${classes.filterButton} ${isActive ? classes.filterButtonActive : ""}`}
        onClick={handleClick}
        size="small"
        title={t("filter")}
      >
        <FilterListIcon className={classes.filterIcon} />
        {isActive && (
          <span className={classes.filterCount}>{selectedTags.length}</span>
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className={classes.menu}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {availableTags.map((tag) => (
          <MenuItem
            key={tag}
            onClick={() => handleToggleTag(tag)}
            className={classes.menuItem}
            dense
          >
            <Checkbox
              checked={selectedTags?.includes(tag) || false}
              className={classes.checkbox}
              style={{ color: clientColor }}
              size="small"
            />
            <ListItemText primary={tag} />
          </MenuItem>
        ))}
        {isActive && (
          <MenuItem
            onClick={handleClearAll}
            className={`${classes.menuItem} ${classes.clearItem}`}
            dense
          >
            {t("all_variants")}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

VariantTagFilter.propTypes = {
  availableTags: PropTypes.array.isRequired,
  selectedTags: PropTypes.array,
  onTagsChange: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
};

VariantTagFilter.defaultProps = {
  selectedTags: [],
};

export default VariantTagFilter;
